import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  CalendarDays,
  CalendarRange,
  CheckCircle2,
  Clock3,
  FileText,
  Send,
  XCircle,
} from "lucide-react";
import GlobalFeedback from "../../../global/GlobalFeedback";
import GlobalSuccessFlashModal from "../../../global/GlobalSuccessFlashModal";
import GlobalDateNavigator from "../../../global/GlobalDateNavigator";
import { GlobalButton, GlobalDateField, GlobalTextField } from "../../../global/forms/GlobalFormFields";
import GlobalMetricCards from "../../../global/GlobalMetricCards";
import { formatDateLabel, todayIso } from "../../../global/dateNavUtils";
import { fetchMyLeaveHistory, applyAttendanceLeave } from "../api/attendanceOperationsApi";
import "../styles/attendance.css";
import "../styles/attendance-apply-leave.css";

function formatLeaveRange(from, to) {
  const a = new Date(`${from}T00:00:00`);
  const b = new Date(`${to}T00:00:00`);
  if (Number.isNaN(a.getTime()) || Number.isNaN(b.getTime())) return `${from} → ${to}`;
  const opts = { day: "numeric", month: "short", year: "numeric" };
  return `${a.toLocaleDateString(undefined, opts)} → ${b.toLocaleDateString(undefined, opts)}`;
}

function countLeaveDays(from, to) {
  const a = new Date(`${from}T00:00:00`);
  const b = new Date(`${to}T00:00:00`);
  if (Number.isNaN(a.getTime()) || Number.isNaN(b.getTime())) return null;
  const diff = Math.round((b - a) / 86400000) + 1;
  return diff > 0 ? diff : null;
}

function statusClass(status) {
  const s = String(status ?? "").toLowerCase();
  if (s === "approved") return "att-leave-status att-leave-status--approved";
  if (s === "rejected") return "att-leave-status att-leave-status--rejected";
  return "att-leave-status att-leave-status--pending";
}

function statusIcon(status) {
  const s = String(status ?? "").toLowerCase();
  if (s === "approved") return CheckCircle2;
  if (s === "rejected") return XCircle;
  return Clock3;
}

export default function AttendanceApplyLeavePage() {
  const [history, setHistory] = useState({ leaves: [], approved_count: 0 });
  const [form, setForm] = useState({ leave_from: "", leave_to: "", reason: "" });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [errorMsg, setErrorMsg] = useState("");
  const [filterDate, setFilterDate] = useState(todayIso);

  const minLeaveDay = todayIso();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchMyLeaveHistory();
      setHistory(data);
    } catch (err) {
      setErrorMsg(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const leaveStats = useMemo(() => {
    const leaves = history.leaves ?? [];
    const pending = leaves.filter((r) => String(r.status).toUpperCase() === "PENDING").length;
    const rejected = leaves.filter((r) => String(r.status).toUpperCase() === "REJECTED").length;
    return {
      approved: history.approved_count ?? 0,
      pending,
      rejected,
      total: leaves.length,
    };
  }, [history]);

  const metrics = useMemo(
    () => [
      { value: leaveStats.approved, label: "Approved", tone: "teal" },
      { value: leaveStats.pending, label: "Pending", tone: "mint" },
      { value: leaveStats.rejected, label: "Rejected", tone: "coral" },
      { value: leaveStats.total, label: "Total", tone: "slate" },
    ],
    [leaveStats],
  );

  const draftDays = useMemo(() => {
    if (!form.leave_from || !form.leave_to) return null;
    return countLeaveDays(form.leave_from, form.leave_to);
  }, [form.leave_from, form.leave_to]);

  const filteredLeaves = useMemo(() => {
    const leaves = history.leaves ?? [];
    return leaves.filter((row) => row.leave_from <= filterDate && filterDate <= row.leave_to);
  }, [history.leaves, filterDate]);

  const rejectPastDate = (value) => {
    const iso = String(value ?? "").trim();
    if (!iso) return "";
    return iso < todayIso() ? "" : iso;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError("");
    const minDay = todayIso();
    if (!form.reason.trim()) {
      setFormError("Please mention a reason for leave.");
      return;
    }
    if (!form.leave_from || !form.leave_to) {
      setFormError("Please select both from and to dates.");
      return;
    }
    if (form.leave_from < minDay || form.leave_to < minDay) {
      setFormError("Leave cannot be applied for past dates.");
      return;
    }
    if (form.leave_to < form.leave_from) {
      setFormError("End date must be on or after the start date.");
      return;
    }
    setSaving(true);
    try {
      await applyAttendanceLeave(form);
      setSuccessMsg("Leave application submitted.");
      setForm({ leave_from: "", leave_to: "", reason: "" });
      load();
    } catch (err) {
      setFormError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="att-shell att-leave-page">
      <GlobalSuccessFlashModal message={successMsg} onDismiss={() => setSuccessMsg("")} />
      <GlobalFeedback type="error" text={errorMsg} />

      <header className="att-leave-toolbar" aria-label="Apply leave">
        <div className="att-leave-toolbar__brand">
          <span className="att-leave-toolbar__icon" aria-hidden>
            <CalendarRange size={18} />
          </span>
          <div>
            <strong>Apply for leave</strong>
            <span>Submit dates and a reason — manager will review</span>
          </div>
        </div>
        <p className="att-leave-toolbar__hint">
          Approved applications: <strong>{loading ? "…" : leaveStats.approved}</strong>
        </p>
      </header>

      <GlobalDateNavigator
        value={filterDate}
        onChange={setFilterDate}
        className="gdn-date-nav--block att-leave-date-nav"
        ariaLabel="Filter leave history by date"
      />

      <GlobalMetricCards
        items={metrics}
        loading={loading}
        gridClassName="att-leave-metrics gm-metrics-grid--quad"
        ariaLabel="Leave application metrics"
      />

      <div className="att-leave-bento">
        <section className="att-leave-panel" aria-labelledby="att-leave-form-title">
          <header className="att-leave-panel__head">
            <span className="att-leave-panel__icon" aria-hidden>
              <FileText size={16} />
            </span>
            <div>
              <h2 id="att-leave-form-title">New application</h2>
              <p>From today onward — pick your leave window</p>
            </div>
          </header>

          <form className="att-leave-panel__body att-leave-form" onSubmit={handleSubmit}>
            <div className="att-leave-form__fields">
              <GlobalDateField
                label="From"
                value={form.leave_from}
                onChange={(v) => {
                  const from = rejectPastDate(v);
                  if (v && !from) {
                    setFormError("Leave cannot start on a past date.");
                    return;
                  }
                  setFormError("");
                  setForm((p) => ({
                    ...p,
                    leave_from: from,
                    leave_to: p.leave_to && p.leave_to < from ? from : p.leave_to,
                  }));
                }}
                min={minLeaveDay}
                required
              />
              <GlobalDateField
                label="To"
                value={form.leave_to}
                onChange={(v) => {
                  const to = rejectPastDate(v);
                  if (v && !to) {
                    setFormError("Leave cannot end on a past date.");
                    return;
                  }
                  setFormError("");
                  setForm((p) => ({ ...p, leave_to: to }));
                }}
                min={form.leave_from || minLeaveDay}
                required
              />
              <GlobalTextField
                label="Reason"
                value={form.reason}
                onChange={(v) => setForm((p) => ({ ...p, reason: v }))}
                placeholder="Why do you need leave?"
                required
              />
            </div>

            {draftDays ? (
              <p className="att-leave-form__duration" aria-live="polite">
                <CalendarDays size={14} aria-hidden />
                {draftDays} day{draftDays === 1 ? "" : "s"} selected
              </p>
            ) : null}

            {formError ? (
              <p className="att-leave-form__error" role="alert">
                {formError}
              </p>
            ) : null}

            <GlobalButton type="submit" variant="primary" disabled={saving} className="att-leave-form__submit">
              <Send size={15} aria-hidden />
              {saving ? "Submitting…" : "Submit application"}
            </GlobalButton>
          </form>
        </section>

        <section className="att-leave-panel" aria-labelledby="att-leave-history-title">
          <header className="att-leave-panel__head">
            <span className="att-leave-panel__icon att-leave-panel__icon--soft" aria-hidden>
              <Clock3 size={16} />
            </span>
            <div>
              <h2 id="att-leave-history-title">Your leave history</h2>
              <p>
                {loading
                  ? "Loading…"
                  : `${filteredLeaves.length} on ${formatDateLabel(filterDate)} · ${history.leaves.length} total`}
              </p>
            </div>
          </header>

          <div className="att-leave-panel__body">
            {loading ? (
              <div className="att-leave-skeleton" aria-busy="true" />
            ) : history.leaves.length === 0 ? (
              <div className="att-leave-empty">
                <CalendarRange size={28} strokeWidth={1.25} aria-hidden />
                <p>No applications yet</p>
                <span>Submit your first request using the form.</span>
              </div>
            ) : filteredLeaves.length === 0 ? (
              <div className="att-leave-empty">
                <CalendarRange size={28} strokeWidth={1.25} aria-hidden />
                <p>No leave on this date</p>
                <span>Try another day or submit a new application.</span>
              </div>
            ) : (
              <ul className="att-leave-history">
                {filteredLeaves.map((row) => {
                  const StatusIcon = statusIcon(row.status);
                  const days = countLeaveDays(row.leave_from, row.leave_to);
                  return (
                    <li key={row.id}>
                      <article className="att-leave-card">
                        <div className="att-leave-card__top">
                          <div className="att-leave-card__dates">
                            <StatusIcon size={15} aria-hidden />
                            <strong>{formatLeaveRange(row.leave_from, row.leave_to)}</strong>
                            {days ? (
                              <span className="att-leave-card__days">
                                {days}d
                              </span>
                            ) : null}
                          </div>
                          <span className={statusClass(row.status)}>{row.status}</span>
                        </div>
                        <p className="att-leave-card__reason">{row.reason}</p>
                        {row.approver_name ? (
                          <p className="att-leave-card__meta">
                            {row.status === "APPROVED" ? "Approved" : "Reviewed"} by {row.approver_name}
                            {row.approved_at
                              ? ` · ${new Date(row.approved_at).toLocaleString()}`
                              : ""}
                          </p>
                        ) : (
                          <p className="att-leave-card__meta">
                            Submitted {row.created_at ? new Date(row.created_at).toLocaleString() : "—"}
                          </p>
                        )}
                        {row.rejection_reason ? (
                          <p className="att-leave-card__reject">Rejected: {row.rejection_reason}</p>
                        ) : null}
                      </article>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </section>
      </div>
    </div>
  );
}
